import { useState, type CSSProperties, type FocusEvent, type ReactNode } from 'react'
import { useIsMemberMobile } from '../../member/useIsMemberMobile'
import { useKeyboardHeight } from '../../hooks/useKeyboardHeight'

export type FloatingInputProps = {
  children: ReactNode
  label: string
  className?: string
  floatingClassName?: string
}

export const FloatingInput = ({
  children,
  label,
  className,
  floatingClassName,
}: FloatingInputProps) => {
  const isMobile = useIsMemberMobile()
  const keyboardHeight = useKeyboardHeight()
  const [isFocused, setIsFocused] = useState(false)

  if (!isMobile) {
    return <div className={className}>{children}</div>
  }

  const isFloating = isFocused && keyboardHeight > 0

  const handleFocusCapture = (event: FocusEvent<HTMLDivElement>) => {
    const target = event.target
    if (
      target instanceof HTMLInputElement ||
      target instanceof HTMLTextAreaElement ||
      (target instanceof HTMLElement && target.isContentEditable)
    ) {
      setIsFocused(true)
    }
  }

  const handleBlurCapture = (event: FocusEvent<HTMLDivElement>) => {
    // If the next active element is still inside this wrapper, stay focused
    const nextTarget = event.relatedTarget
    if (nextTarget instanceof Node && event.currentTarget.contains(nextTarget)) {
      return
    }
    setIsFocused(false)
  }

  const wrapperStyle: CSSProperties = isFloating
    ? {
        position: 'fixed',
        left: '12px',
        right: '12px',
        // windowSoftInputMode="adjustResize": the viewport bottom already sits
        // above the open keyboard, so this only needs the safe-area inset —
        // adding the keyboard-height custom property here would over-offset
        // by the full keyboard height.
        bottom: 'env(safe-area-inset-bottom, 0px)',
        zIndex: 1000,
        padding: '8px 12px 12px',
        background: 'var(--surface-raised)',
        borderTop: '1px solid var(--border)',
        boxShadow: 'var(--shadow-pop)',
        transition: 'bottom 200ms cubic-bezier(0.32, 0.72, 0, 1)',
      }
    : {
        position: 'relative',
      }

  const labelStyle: CSSProperties = {
    fontFamily: 'var(--font-figures)',
    fontSize: '11px',
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: 'var(--muted)',
    marginBottom: '4px',
  }

  const combinedClassName = [
    'input-wrapper',
    className,
    isFloating ? 'floating' : '',
    isFloating && floatingClassName ? floatingClassName : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div
      className={combinedClassName}
      style={wrapperStyle}
      onFocusCapture={handleFocusCapture}
      onBlurCapture={handleBlurCapture}
    >
      {isFloating ? <div className="field-label" style={labelStyle}>{label}</div> : null}
      {children}
    </div>
  )
}
